import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { api, ApiError, getToken, qs } from '../api/client';
import { useAuth } from '../state/auth';
import { Badge, Bandeau, Carte, Champ, Modale, Squelette } from '../components/ui';
import { urlImpression, urlApi } from '../api/config';
import { Signature } from '../components/SchemaVoiture';
import { centimes, deviseReference } from '../lib/format';
import { dateFr, dateHeureFr, montant, nomClient } from '../lib/format';
import { T, langue, reglagesRegionaux } from '../i18n';
import type { Client, Contrat, Reservation as Resa, Vehicule } from '../api/types';

/** Ce qui touche a l'argent doit etre tranche par Hichem, pas par Reda. */
const EST_FINANCIER = /prix|tarif|montant|solde|caution|supplément|supplement|remise|acompte|facture|total|devise/i;

export default function Reservation() {
  const { id } = useParams();
  const nav = useNavigate();
  const { peut, estAgent } = useAuth();
  const L = T.reservation;
  const u = reglagesRegionaux().uniteDistance;
  const nb = (x: number) => x.toLocaleString(langue === 'fr' ? 'fr-FR' : 'en-US');
  const CARBURANT_LABEL = T.etatDesLieux.carburantQuarts;
  const [r, setR] = useState<Resa | null>(null);
  const [contrat, setContrat] = useState<Contrat | null>(null);
  const [caution, setCaution] = useState<{ _id: string; montantMinor: number; devise: string; statut: string; dechargeRemiseNumero: string; dechargeRestitutionNumero?: string } | null>(null);
  const [demande, setDemande] = useState(false);
  const [valeurActuelle, setValeurActuelle] = useState('');
  const [annulation, setAnnulation] = useState(false);
  const [prolongation, setProlongation] = useState(false);
  const [nouvelleFin, setNouvelleFin] = useState('');
  const [apercu, setApercu] = useState<string>('');
  const [resiliation, setResiliation] = useState(false);
  const [motifAction, setMotifAction] = useState('');
  const [saisirCaution, setSaisirCaution] = useState(false);
  /*
   * RENDRE LA CAUTION SANS QUITTER LA LOCATION.
   *
   * La restitution n'existait que dans l'écran « Cautions ». Au retour d'une
   * voiture, on est sur la location : il fallait retenir le numéro de décharge,
   * changer d'écran et retrouver la ligne. Reda l'a signalé — le geste se fait
   * maintenant là où il se produit.
   */
  /*
   * SIGNALER UN CHANGEMENT DE DATES.
   *
   * « Sabrina a un nouveau bouton pour signaler si des dates d'une location ont
   * changé, et Hichem a le droit de valider dans À valider. » Au comptoir, on
   * apprend que le client rend la voiture deux jours plus tard ; la saisie des
   * dates reste aux gérants, mais l'information ne doit pas se perdre dans un
   * appel téléphonique. Accordée, la demande applique les dates elle-même.
   */
  const [signaler, setSignaler] = useState(false);
  const [nvDebut, setNvDebut] = useState('');
  const [nvFin, setNvFin] = useState('');
  const [motifDates, setMotifDates] = useState('');
  const [rendre, setRendre] = useState(false);
  const [retenu, setRetenu] = useState('0');
  const [motifRetenue, setMotifRetenue] = useState('');
  const [sigClient, setSigClient] = useState('');
  const [sigAgent, setSigAgent] = useState('');
  const [envoiAction, setEnvoiAction] = useState(false);
  const [factures, setFactures] = useState<{ _id: string; numero: string; type: string; date: string; totalMinor: number }[]>([]);
  const [valeurDemandee, setValeurDemandee] = useState('');
  const [caut, setCaut] = useState(false);
  const [cf, setCf] = useState({ montant: '', devise: deviseReference(), forme: 'especes', piece: '', sigC: '', sigA: '' });
  /* La caution annoncee au client a la creation : on la repropose plutot que
   * de demander a l'agent de s'en souvenir. Il peut toujours la corriger. */
  useEffect(() => {
    if (r?.cautionPrevueMinor && !cf.montant) {
      setCf((x) => ({ ...x, montant: String((r.cautionPrevueMinor ?? 0) / 100) }));
    }
  }, [r?.cautionPrevueMinor, cf.montant]);
  const [champ, setChamp] = useState('');
  const [motif, setMotif] = useState('');
  const [msg, setMsg] = useState('');
  /* La location a bien ete creee, mais la caution n'a pas pu suivre.
   * On le dit ici plutot que de laisser l'agent croire que tout est fait. */
  const [params] = useSearchParams();
  const cautionAReprendre = params.get('caution') === 'a_reprendre';

  useEffect(() => {
    if (!id) return;
    api.get<Resa>(`/reservations/${id}`).then(async (res) => {
      setR(res);
      const [c] = await api.get<Contrat[]>(`/contrats${qs({ reservation: res._id })}`);
      if (c) {
        const d = await api.get<{ contrat: Contrat; caution: typeof caution }>(`/contrats/${c._id}`);
        setContrat(d.contrat); setCaution(d.caution);
        api.get<typeof factures>(`/factures${qs({ contract: c._id })}`).then(setFactures).catch(() => {});
      }
    });
  }, [id]);

  if (!r) return <Squelette lignes={6} />;
  const v = r.vehicle as Vehicule;
  const c = r.customer as Client;

  async function ouvrir(sens: 'depart' | 'retour') {
    const ct = contrat ?? (await api.post<Contrat>('/contrats', { reservation: r!._id }));
    nav(`/contrats/${ct._id}/etat-des-lieux/${sens}`);
  }

  /**
   * L'ordre reel du comptoir : on edite le contrat, on le fait signer, on
   * encaisse la caution, et seulement ensuite on descend faire l'etat des
   * lieux. Tant que ce bouton n'existait pas, le seul moyen d'obtenir un
   * contrat imprimable etait de commencer l'etat des lieux — une saisie
   * irreversible — puis de revenir en arriere.
   */
  /** Rendre la caution depuis la location, et rouvrir la décharge signée. */
  /** Envoyer les nouvelles dates à « À valider », sans toucher à la location. */
  async function envoyerChangementDates() {
    if (!r) return;
    setMsg('');
    try {
      await api.post('/corrections', {
        entite: 'Reservation', entiteId: r._id, libelleEntite: `Location ${r.numero}`,
        champ: 'dates',
        valeurActuelle: `${dateFr(r.dateDebut)} → ${dateFr(r.dateFin)}`,
        valeurDemandee: `${nvDebut ? dateFr(nvDebut) : dateFr(r.dateDebut)} → ${nvFin ? dateFr(nvFin) : dateFr(r.dateFin)}`,
        motif: motifDates,
        donnees: { dateDebut: nvDebut || undefined, dateFin: nvFin || undefined },
      });
      setSignaler(false); setNvDebut(''); setNvFin(''); setMotifDates('');
      setMsg(L.datesSignale);
    } catch (e) { setMsg(e instanceof ApiError ? e.erreur : L.signalementEchec); }
  }

  async function restituerCaution() {
    if (!caution) return;
    setMsg('');
    try {
      await api.post(`/cautions/${caution._id}/restituer`, {
        montantRetenuMinor: centimes(Number(retenu || 0)),
        motifRetenue, signatureClient: sigClient, signatureAgent: sigAgent,
      });
      setRendre(false); setRetenu('0'); setMotifRetenue(''); setSigClient(''); setSigAgent('');
      setMsg(L.cautionTraitee);
      window.open(urlImpression(`/cautions/${caution._id}/imprimer/restitution`, getToken()), '_blank');
      if (contrat) {
        const d = await api.get<{ contrat: Contrat; caution: typeof caution }>(`/contrats/${contrat._id}`);
        setContrat(d.contrat); setCaution(d.caution);
      }
    } catch (e) { setMsg(e instanceof ApiError ? e.erreur : L.restitutionEchec); }
  }

  async function genererContrat() {
    if (contrat) return;
    setContrat(await api.post<Contrat>('/contrats', { reservation: r!._id }));
    setMsg(L.contratGenere);
  }

  async function creerCaution() {
    if (!contrat) return;
    const d = await api.post<NonNullable<typeof caution>>('/cautions', {
      contract: contrat._id, montantMinor: centimes(Number(cf.montant)),
      devise: cf.devise, forme: cf.forme, numeroPiece: cf.piece || undefined,
      signatureClient: cf.sigC, signatureAgent: cf.sigA,
    });
    setCaution(d); setCaut(false);
    setMsg(L.cautionEnregistree(d.dechargeRemiseNumero));
    window.open(urlImpression(`/cautions/${d._id}/imprimer/remise`, getToken()), '_blank');
  }

  /**
   * Une demande de correction sans les deux valeurs n'est pas exploitable : le
   * gerant recoit « champ : km au depart » et un motif en texte libre, sans
   * savoir quoi mettre. Et le journal enregistrait `avant: {}` / `apres: {}`
   * alors que l'ecran promet que « les deux valeurs restent au journal ».
   */
  /**
   * Annulation et resiliation existaient cote serveur — avec leurs droits, leur
   * motif obligatoire et leur trace au journal — mais aucun bouton nulle part.
   * L'article 07 prevoit la perte de la caution en cas de manquement : c'est la
   * case a cocher de la resiliation.
   */
  async function annuler() {
    setEnvoiAction(true);
    try {
      await api.post(`/reservations/${r!._id}/annuler`, { motif: motifAction.trim() });
      setAnnulation(false); setMotifAction('');
      setMsg(L.annulee);
      setR(await api.get<Resa>(`/reservations/${r!._id}`));
    } catch (e) { setMsg(e instanceof ApiError ? e.erreur : L.annulationImpossible); }
    finally { setEnvoiAction(false); }
  }

  /**
   * Prolonger : le client garde la voiture plus longtemps.
   *
   * On n'allonge que vers l'avant — le serveur refuse une date ramenee en
   * arriere. C'est ce qui rend le bouton sur au comptoir : il ne peut jamais
   * faire baisser une facture, seulement enregistrer des jours reellement
   * consommes.
   */
  async function prolonger() {
    setEnvoiAction(true);
    try {
      const rep = await api.post<{ reservation: Resa; joursAjoutes: number; contrat: Contrat | null }>(
        `/reservations/${r!._id}/prolonger`,
        { nouvelleDateFin: `${nouvelleFin}T18:00:00`, motif: motifAction.trim() || undefined },
      );
      setProlongation(false); setNouvelleFin(''); setMotifAction('');
      setMsg(L.prolongee(rep.joursAjoutes, montant(rep.reservation.totalPrevuMinor)));
      setR(await api.get<Resa>(`/reservations/${r!._id}`));
      if (rep.contrat) setContrat((await api.get<{ contrat: Contrat }>(`/contrats/${rep.contrat._id}`)).contrat);
    } catch (e) { setMsg(e instanceof ApiError ? e.erreur : L.prolongationImpossible); }
    finally { setEnvoiAction(false); }
  }

  async function resilier() {
    if (!contrat) return;
    setEnvoiAction(true);
    try {
      await api.post(`/contrats/${contrat._id}/resilier`, { motif: motifAction.trim(), saisirCaution });
      setResiliation(false); setMotifAction(''); setSaisirCaution(false);
      setMsg(L.resilie(saisirCaution));
      const [c] = await api.get<Contrat[]>(`/contrats${qs({ reservation: r!._id })}`);
      if (c) setContrat((await api.get<{ contrat: Contrat }>(`/contrats/${c._id}`)).contrat);
    } catch (e) { setMsg(e instanceof ApiError ? e.erreur : L.resiliationImpossible); }
    finally { setEnvoiAction(false); }
  }

  async function envoyerDemande() {
    await api.post('/corrections', {
      // 'Reservation' sans accent : c'est ce que le serveur ecrit partout
      // ailleurs, et le filtre du journal cherche cette valeur exacte.
      entite: 'Reservation', entiteId: r!._id, libelleEntite: `Reservation ${r!.numero}`,
      champ, valeurActuelle, valeurDemandee, motif,
      // Une correction qui porte sur de l'argent doit reveiller le garde-fou
      // « seul Hichem tranche une donnee financiere ».
      natureFinanciere: EST_FINANCIER.test(champ),
    });
    setDemande(false); setMotif(''); setChamp('');
    setValeurActuelle(''); setValeurDemandee('');
    setMsg(L.demandeEnvoyee);
  }

  return (
    <>
      <div className="entete">
        <div className="titres">
          <h1>{r.numero} <Badge statut={r.statut} /></h1>
          <p>{v.marque} {v.modele} · {v.immatriculation} — {nomClient(c)} · {dateFr(r.dateDebut)} → {dateFr(r.dateFin)}</p>
        </div>
        <div className="actions">
          {!contrat && peut('creer.contrat') && (
            <button className="btn" onClick={() => void genererContrat()}>{L.genererContrat}</button>
          )}
          {contrat && <a className="btn" target="_blank" rel="noreferrer" href={urlImpression(`/contrats/${contrat._id}/imprimer`, getToken())}>{L.imprimerContrat}</a>}
          {contrat && !caution && <button className="btn" onClick={() => setCaut(true)}>{L.enregistrerCaution}</button>}
          {!peut('modifier.reservation') && !r.verrouille && (
            <button className="btn" onClick={() => setSignaler(true)}>{L.signalerDates}</button>
          )}
          {caution?.statut === 'detenue' && peut('restituer.caution') && (
            <button className="btn" onClick={() => setRendre(true)}>
              {L.restituerCaution(montant(caution.montantMinor, caution.devise))}
            </button>
          )}
          {contrat && contrat.soldeMinor > 0 && peut('creer.mouvementCaisse') && (
            <Link className="btn" to={`/caisse?contrat=${contrat._id}`}>{L.encaisserSolde(montant(contrat.soldeMinor))}</Link>
          )}
          {!contrat?.remise && <button className="btn btn-p" onClick={() => void ouvrir('depart')}>{L.faireDepart}</button>}
          {contrat?.remise && !contrat?.restitution && <button className="btn btn-p" onClick={() => void ouvrir('retour')}>{L.faireRetour}</button>}
          {/*
            * CORRIGER UN RETOUR DÉJÀ SAISI.
            *
            * Le serveur l'acceptait pour qui peut corriger un état des lieux, mais
            * aucun bouton n'y menait : Sabrina, qui avait noté une mauvaise heure de
            * retour (33 000 DZD de retard pour un client rendu à l'heure), a dû passer
            * par « À valider » — où une demande de dates ne pouvait rien changer.
            * Refaire le retour recalcule les suppléments, retard compris.
            */}
          {contrat?.restitution && peut('modifier.etatDesLieux') && (
            <button className="btn" onClick={() => void ouvrir('retour')}>{L.corrigerRetour}</button>
          )}
          {/*
            * Prolonger, annuler, resilier : disponibles a tout moment.
            *
            * L'annulation disparaissait des que la voiture etait remise et sur
            * toute location terminee — or c'est justement la qu'on en a besoin :
            * une location saisie sur la mauvaise voiture, un client qui ne s'est
            * jamais presente apres le depart enregistre. Le seul etat qui ferme
            * la porte est « deja annulee ».
            */}
          {peut('creer.reservation') && r.statut !== 'annulee' && (
            <button className="btn" onClick={() => {
              setProlongation(true); setMotifAction('');
              setNouvelleFin(String(r.dateFin).slice(0, 10)); setApercu('');
            }}>{L.prolongerLocation}</button>
          )}
          {peut('annuler.reservation') && r.statut !== 'annulee' && (
            <button className="btn btn-d" onClick={() => { setAnnulation(true); setMotifAction(''); }}>{L.annulerLocation}</button>
          )}
          {peut('resilier.contrat') && contrat && contrat.statut !== 'resilie' && (
            <button className="btn btn-d" onClick={() => { setResiliation(true); setMotifAction(''); }}>{L.resilierContrat}</button>
          )}
        </div>
      </div>

      {msg && <div style={{ marginBottom: 14 }}><Bandeau type="ok">{msg}</Bandeau></div>}
      {cautionAReprendre && !caution && (
        <div style={{ marginBottom: 14 }}>
          <Bandeau type="warn" titre={L.cautionNonEnregistree}>
            {L.cautionNonEnregistreeTexte}
          </Bandeau>
        </div>
      )}
      {c.listeNoire && <div style={{ marginBottom: 14 }}><Bandeau type="ko" titre={L.listeNoire}>{c.motifListeNoire}</Bandeau></div>}
      {(r.derogations ?? []).map((d, i) => (
        <div key={i} style={{ marginBottom: 14 }}>
          <Bandeau type="warn" titre={L.derogation(d.detail)}>{L.derogationDetail(d.motif, dateFr(d.date))}</Bandeau>
        </div>
      ))}

      {/*
        * Tout ce qui s'imprime, au meme endroit.
        *
        * Le contrat etait imprimable ici, mais la decharge de RESTITUTION — celle
        * qu'on signe en rendant la caution, et qui est l'une des trois pieces qui
        * protegent l'agence — n'etait atteignable que depuis un autre ecran. Les
        * factures du dossier n'etaient nulle part.
        */}
      {contrat && (
        <div style={{ marginBottom: 16 }}>
          <Carte titre={L.documents} pad={false}>
            <div className="enveloppe-tbl">
              <table className="tbl compact">
                <tbody>
                  <tr>
                    <td><b>{L.contratLocation}</b><div className="mini sourdine">{L.contratDetail(contrat.numero)}</div></td>
                    <td className="droite">
                      <a className="btn btn-s" target="_blank" rel="noreferrer"
                         href={urlImpression(`/contrats/${contrat._id}/imprimer`, getToken())}>{L.imprimer}</a>
                    </td>
                  </tr>
                  {caution && (
                    <tr>
                      <td>
                        <b>{L.dechargeRemise}</b>
                        <div className="mini sourdine">{L.dechargeRemiseDetail(caution.dechargeRemiseNumero, montant(caution.montantMinor))}</div>
                      </td>
                      <td className="droite">
                        <a className="btn btn-s" target="_blank" rel="noreferrer"
                           href={urlImpression(`/cautions/${caution._id}/imprimer/remise`, getToken())}>{L.imprimer}</a>
                      </td>
                    </tr>
                  )}
                  {caution?.dechargeRestitutionNumero && (
                    <tr>
                      <td>
                        <b>{L.dechargeRestitution}</b>
                        <div className="mini sourdine">{L.numero(caution.dechargeRestitutionNumero)}<Badge statut={caution.statut} /></div>
                      </td>
                      <td className="droite">
                        <a className="btn btn-s" target="_blank" rel="noreferrer"
                           href={urlImpression(`/cautions/${caution._id}/imprimer/restitution`, getToken())}>{L.imprimer}</a>
                      </td>
                    </tr>
                  )}
                  {factures.map((fa) => (
                    <tr key={fa._id}>
                      <td>
                        <b>{L.facture(fa.type)}</b>
                        <div className="mini sourdine">{L.factureDetail(fa.numero, dateFr(fa.date), montant(fa.totalMinor))}</div>
                      </td>
                      <td className="droite">
                        <a className="btn btn-s" target="_blank" rel="noreferrer"
                           href={urlImpression(`/factures/${fa._id}/imprimer`, getToken())}>{L.imprimer}</a>
                      </td>
                    </tr>
                  ))}
                  {!caution && factures.length === 0 && (
                    <tr>
                      <td colSpan={2} className="mini sourdine">
                        {L.dechargeApparaitra}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Carte>
        </div>
      )}

      <div className="grille g2" style={{ alignItems: 'start' }}>
        <Carte titre={L.location}>
          <table className="tbl compact">
            <tbody>
              {/* Au mois, le prix se lit en MOIS. Afficher un tarif journalier
                  ici montrerait l'équivalent arrondi — un chiffre juste pour les
                  statistiques, mais que le client n'a jamais accepté. */}
              {r.modeTarification === 'mois' ? (
                <>
                  <tr><td>{L.prixMois}</td><td className="droite montant">{montant(r.tarifMoisNegocieMinor ?? 0)}</td></tr>
                  <tr><td>{L.nMois(r.nbMoisFactures)}<span className="mini sourdine">{L.nJours(r.joursFactures)}</span></td>
                    <td className="droite montant">{montant(r.totalLocationMinor)}</td></tr>
                </>
              ) : (
                <>
                  <tr><td>{L.tarifReference}</td><td className="droite montant">{L.parJour(montant(r.tarifReferenceMinor))}</td></tr>
                  <tr><td>{L.prixNegocie}</td><td className="droite montant">{L.parJour(montant(r.tarifNegocieMinor))}
                    {r.tarifNegocieMinor !== r.tarifReferenceMinor && (
                      <div className="mini sourdine" style={{ fontWeight: 400 }}>
                        {Math.round(((r.tarifNegocieMinor - r.tarifReferenceMinor) / r.tarifReferenceMinor) * 1000) / 10} %
                      </div>)}
                  </td></tr>
                  <tr><td>{L.joursSaison(r.joursFactures, L.saisons[r.saison as string] ?? r.saison)}</td><td className="droite montant">{montant(r.totalLocationMinor)}</td></tr>
                </>
              )}
              <tr><td>{L.kilometrage}</td><td className="droite">{r.kmIllimite ? L.illimite : L.forfaitJour(r.forfaitKmJour, u)}</td></tr>
              {/* Sans cette ligne, le credit accorde au comptoir devenait invisible
                * des qu'on quittait l'ecran de creation. */}
              <tr><td>{L.paiement}</td><td className="droite">
                {r.reglement === 'differe'
                  ? <b style={{ color: 'var(--danger)' }}>{L.differe}</b>
                  : r.reglement === 'par_tranche'
                    ? <b style={{ color: 'var(--attention)' }}>{L.parTranches}</b>
                    : L.surPlace}
              </td></tr>
              {(r.tvaMinor ?? 0) > 0 && (
                <>
                  <tr><td>{L.totalHt}</td><td className="droite montant">{montant(r.totalHtMinor ?? 0)}</td></tr>
                  <tr><td>{L.tva(r.tauxTvaPct)}</td><td className="droite montant">{montant(r.tvaMinor ?? 0)}</td></tr>
                </>
              )}
              <tr><td><b>{L.totalPrevu((r.tvaMinor ?? 0) > 0)}</b></td>
                  <td className="droite montant"><b>{montant(r.totalPrevuMinor)}</b></td></tr>
            </tbody>
          </table>
        </Carte>

        <Carte titre={L.contratCaution}>
          {!contrat ? (
            <p className="sourdine mini">{L.aucunContrat}</p>
          ) : (
            <table className="tbl compact">
              <tbody>
                <tr><td>{L.numeroLabel}</td><td className="droite"><b>{contrat.numero}</b> <Badge statut={contrat.statut} /></td></tr>
                <tr><td>{L.edlDepart}</td><td className="droite">{contrat.remise ? dateHeureFr(contrat.remise.dateHeure) : '—'}</td></tr>
                <tr><td>{L.edlRetour}</td><td className="droite">{contrat.restitution ? dateHeureFr(contrat.restitution.dateHeure) : '—'}</td></tr>
                <tr><td>{L.encaisse}</td><td className="droite montant">{montant(contrat.totalEncaisseMinor)}</td></tr>
                <tr><td><b>{L.soldeDu}</b></td><td className="droite montant"><b>{montant(contrat.soldeMinor)}</b></td></tr>
                {caution && (
                  <tr><td>{L.caution}</td><td className="droite montant">{montant(caution.montantMinor, caution.devise)}
                    <div className="mini sourdine" style={{ fontWeight: 400 }}>{caution.dechargeRemiseNumero} · <Badge statut={caution.statut} /></div>
                  </td></tr>
                )}
              </tbody>
            </table>
          )}
        </Carte>
      </div>

      {contrat?.supplementsRetour?.length ? (
        <div style={{ marginTop: 16 }}>
          <Carte titre={L.supplementsRetour} pad={false}>
            <table className="tbl">
              <thead><tr><th>{L.colSupplement}</th><th>{L.colCalcul}</th><th className="droite">{L.colMontant}</th></tr></thead>
              <tbody>
                {contrat.supplementsRetour.map((l) => (
                  <tr key={l.code}>
                    <td><b>{l.libelle}</b> {l.modifieManuellement && <Badge statut="retour" libelle={L.modifieManuellement} />}</td>
                    <td className="mini sourdine">{l.formule}</td>
                    <td className="droite montant">{montant(l.totalMinor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Carte>
        </div>
      ) : null}

      {contrat?.remise && (
        <div style={{ marginTop: 16 }}>
          <Carte titre={L.etatDesLieux}>
            <div className="grille g2">
              {(['remise', 'restitution'] as const).map((k) => {
                const e = contrat[k];
                return (
                  <div key={k}>
                    <div className="micro">{k === 'remise' ? L.depart : L.retour}</div>
                    {!e ? <p className="sourdine mini">{L.pasEncore}</p> : (
                      <table className="tbl compact">
                        <tbody>
                          <tr><td>{L.date}</td><td className="droite">{dateHeureFr(e.dateHeure)}</td></tr>
                          <tr><td>{L.kilometrage}</td><td className="droite num">{nb(e.km)} {u}</td></tr>
                          <tr><td>{L.carburant}</td><td className="droite">{CARBURANT_LABEL[e.carburantQuarts]}</td></tr>
                          <tr><td>{L.proprete}</td><td className="droite">{L.propreteValeur(e.propreteInterieur, e.propreteExterieur)}</td></tr>
                          <tr><td>{L.dommages}</td><td className="droite">{e.dommages.length}{e.dommages.some((d) => d.nouveau) ? L.dontNouveaux(e.dommages.filter((d) => d.nouveau).length) : ''}</td></tr>
                          <tr><td>{L.photos}</td><td className="droite">{e.photos.length} / 6</td></tr>
                        </tbody>
                      </table>
                    )}
                    {e && e.photos.length > 0 && (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 6, marginTop: 8 }}>
                        {e.photos.map((p, i) => (
                          <a key={i} href={urlApi(p)} target="_blank" rel="noreferrer"
                             style={{ aspectRatio: '4/3', borderRadius: 6, background: `center/cover no-repeat url(${urlApi(p)})`, border: '1px solid var(--bord)' }}
                             title={L.ouvrirGrand} />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Carte>
        </div>
      )}

      {estAgent && (
        <div style={{ marginTop: 16 }}>
          <Bandeau type="info" titre={L.saisieDefinitive}>
            {L.saisieDefinitiveTexte}
            <div style={{ marginTop: 10 }}><button className="btn btn-s" onClick={() => setDemande(true)}>{L.demanderCorrection}</button></div>
          </Bandeau>
        </div>
      )}
      {/* Le lien vers le planning etait conditionne au droit d'ANNULER et cache
        * a l'agent — deux restes sans rapport avec ce qu'il fait. Il depend
        * maintenant du seul droit qui le gouverne. */}
      {peut('voir.planning') && r.statut !== 'annulee' && (
        <div style={{ marginTop: 16 }}>
          <Link className="btn" to="/planning">{L.voirPlanning}</Link>
        </div>
      )}

      {signaler && (
        <Modale titre={L.signalerDates} onFermer={() => setSignaler(false)}>
          <Bandeau type="info" titre={L.actuellement(dateFr(r.dateDebut), dateFr(r.dateFin))}>
            {L.rienModifie}
          </Bandeau>
          <div className="grille g2" style={{ marginTop: 14 }}>
            <Champ label={L.nouveauDepart} aide={L.laissezVide}>
              <input type="datetime-local" value={nvDebut} onChange={(e) => setNvDebut(e.target.value)} />
            </Champ>
            <Champ label={L.nouveauRetour} aide={L.laissezVide}>
              <input type="datetime-local" value={nvFin} onChange={(e) => setNvFin(e.target.value)} />
            </Champ>
          </div>
          <div style={{ marginTop: 10 }}>
            <Champ label={L.cePasse}>
              <textarea value={motifDates} onChange={(e) => setMotifDates(e.target.value)}
                        placeholder={L.cePassePlaceholder} />
            </Champ>
          </div>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setSignaler(false)}>{L.annuler}</button>
            <button className="btn btn-p"
                    disabled={motifDates.trim().length < 3 || (!nvDebut && !nvFin)}
                    onClick={() => void envoyerChangementDates()}>{L.envoyerAValider}</button>
          </div>
        </Modale>
      )}

      {/* Rendre la caution : même geste et mêmes garde-fous que l'écran « Cautions ». */}
      {rendre && caution && (
        <Modale titre={L.restituerTitre(caution.dechargeRemiseNumero)} onFermer={() => setRendre(false)}>
          <Bandeau type="info" titre={L.detenus(montant(caution.montantMinor, caution.devise))}>
            {L.appartientClient}
          </Bandeau>
          <div style={{ marginTop: 14, display: 'grid', gap: 12 }}>
            <Champ label={L.montantRetenu} aide={L.montantRetenuAide}>
              <input type="number" min={0} value={retenu} onChange={(e) => setRetenu(e.target.value)} />
            </Champ>
            {Number(retenu) > 0 && (
              <Champ label={L.motifRetenue}>
                <textarea value={motifRetenue} onChange={(e) => setMotifRetenue(e.target.value)}
                          placeholder={L.motifRetenuePlaceholder} />
              </Champ>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: 10, borderTop: '1px solid var(--bord)' }}>
              <b>{L.aRestituer}</b>
              <b className="montant" style={{ fontSize: 20 }}>
                {montant(caution.montantMinor - centimes(Number(retenu || 0)), caution.devise)}
              </b>
            </div>
            <Signature label={L.sigClient} valeur={sigClient} onChange={setSigClient} />
            <Signature label={L.sigDirecteur} valeur={sigAgent} onChange={setSigAgent} />
          </div>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setRendre(false)}>{L.annuler}</button>
            <button className="btn btn-p"
                    disabled={Number(retenu) > 0 && motifRetenue.trim().length < 3}
                    onClick={() => void restituerCaution()}>
              {L.restituerEditer}
            </button>
          </div>
        </Modale>
      )}

      {caut && (
        <Modale titre={L.enregistrerCaution} onFermer={() => setCaut(false)}>
          <p className="mini sourdine" style={{ marginBottom: 12 }}>
            {L.cautionPasFixe}
          </p>
          <div style={{ display: 'grid', gap: 12 }}>
            <div className="grille g2">
              <Champ label={L.montant}><input type="number" value={cf.montant} onChange={(e) => setCf({ ...cf, montant: e.target.value })} autoFocus /></Champ>
              <Champ label={L.devise}>
                <select value={cf.devise} onChange={(e) => setCf({ ...cf, devise: e.target.value })}>
                  {Array.from(new Set([deviseReference(), 'DZD', 'EUR', 'USD', 'TND'])).map((d) => <option key={d}>{d}</option>)}
                </select>
              </Champ>
            </div>
            <div className="grille g2">
              <Champ label={L.forme}>
                <select value={cf.forme} onChange={(e) => setCf({ ...cf, forme: e.target.value })}>
                  <option value="especes">{L.formes.especes}</option><option value="cheque">{L.formes.cheque}</option>
                  <option value="traite">{L.formes.traite}</option><option value="carte">{L.formes.carte}</option>
                </select>
              </Champ>
              <Champ label={L.numeroPiece} aide={L.numeroPieceAide}>
                <input value={cf.piece} onChange={(e) => setCf({ ...cf, piece: e.target.value })} />
              </Champ>
            </div>
            <Signature label={L.sigClient} valeur={cf.sigC} onChange={(v) => setCf({ ...cf, sigC: v })} />
            <Signature label={L.sigDirecteur} valeur={cf.sigA} onChange={(v) => setCf({ ...cf, sigA: v })} />
          </div>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setCaut(false)}>{L.annuler}</button>
            <button className="btn btn-p" disabled={!Number(cf.montant)} onClick={() => void creerCaution()}>
              {L.enregistrerEditer}
            </button>
          </div>
        </Modale>
      )}

      {prolongation && (
        <Modale titre={L.prolongerLocation} onFermer={() => setProlongation(false)}>
          <p className="mini sourdine" style={{ marginTop: 0 }}>
            {L.retourPrevuA}<b>{dateHeureFr(r.dateFin)}</b>{L.retourPrevuB(r.joursFactures)}
            {' '}{montant(r.totalPrevuMinor)}.
          </p>
          <Champ label={L.nouveauRetour}
                 aide={r.modeTarification === 'mois'
                   ? L.moisRecalcule
                   : L.doitEtreApres}>
            <input type="date" value={nouvelleFin} min={String(r.dateFin).slice(0, 10)}
                   onChange={(e) => {
                     setNouvelleFin(e.target.value);
                     const j = Math.round(
                       (new Date(`${e.target.value}T18:00:00`).getTime() - new Date(r.dateFin).getTime()) / 86400000,
                     );
                     setApercu(j > 0
                       ? (r.modeTarification === 'mois'
                         // Au mois, prolonger ajoute des MOIS : annoncer un prix
                         // au journalier promettrait au client un montant faux.
                         ? L.apercuMois
                         : L.apercuJours(j, montant(r.totalPrevuMinor + j * r.tarifNegocieMinor)))
                       : '');
                   }} />
          </Champ>
          {apercu && <p className="mini" style={{ marginTop: 6, color: 'var(--primaire)' }}>{apercu}</p>}
          <div style={{ marginTop: 12 }}>
            <Champ label={L.motif} aide={L.motifAide}>
              <input value={motifAction} onChange={(e) => setMotifAction(e.target.value)}
                     placeholder={L.motifProlongationPlaceholder} />
            </Champ>
          </div>
          <div style={{ marginTop: 12 }}>
            <Bandeau type="warn" titre={L.doitEtreLibre}>
              {L.dejaPromise}
            </Bandeau>
          </div>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setProlongation(false)}>{L.revenir}</button>
            <button className="btn btn-p"
                    disabled={envoiAction || !nouvelleFin || nouvelleFin <= String(r.dateFin).slice(0, 10)}
                    onClick={() => void prolonger()}>{L.prolonger}</button>
          </div>
        </Modale>
      )}

      {annulation && (
        <Modale titre={L.annulerTitre} onFermer={() => setAnnulation(false)}>
          <Bandeau type="warn" titre={L.motifDefinitif}>
            {L.resteJournal}
          </Bandeau>
          {(contrat?.remise || (contrat?.totalEncaisseMinor ?? 0) > 0) && (
            <div style={{ marginTop: 12 }}>
              <Bandeau type="ko" titre={L.dejaCommence}>
                {contrat?.remise && <>{L.voitureSortie}</>}
                {(contrat?.totalEncaisseMinor ?? 0) > 0 && (
                  <>{L.dejaEncaisses(montant(contrat!.totalEncaisseMinor))}</>
                )}
                {L.plutotResilierA}<b>{L.plutotResilierB}</b>{L.plutotResilierC}
              </Bandeau>
            </div>
          )}
          <div style={{ marginTop: 12 }}>
            <Champ label={L.motifAnnulation}>
              <textarea value={motifAction} onChange={(e) => setMotifAction(e.target.value)}
                        placeholder={L.motifAnnulationPlaceholder} />
            </Champ>
          </div>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setAnnulation(false)}>{L.revenir}</button>
            <button className="btn btn-d" disabled={motifAction.trim().length < 3 || envoiAction}
                    onClick={() => void annuler()}>{L.annulerLocation}</button>
          </div>
        </Modale>
      )}

      {resiliation && (
        <Modale titre={L.resilierContrat} onFermer={() => setResiliation(false)}>
          <Bandeau type="ko" titre={L.resiliationManquement}>
            {L.article07}
          </Bandeau>
          <div style={{ marginTop: 12 }}>
            <Champ label={L.motifResiliation}>
              <textarea value={motifAction} onChange={(e) => setMotifAction(e.target.value)}
                        placeholder={L.motifResiliationPlaceholder} />
            </Champ>
          </div>
          <label style={{ display: 'flex', gap: 9, alignItems: 'center', marginTop: 12 }}>
            <input type="checkbox" checked={saisirCaution} onChange={(e) => setSaisirCaution(e.target.checked)} />
            {L.saisirCaution}
          </label>
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setResiliation(false)}>{L.revenir}</button>
            <button className="btn btn-d" disabled={motifAction.trim().length < 3 || envoiAction}
                    onClick={() => void resilier()}>{L.resilier}</button>
          </div>
        </Modale>
      )}

      {demande && (
        <Modale titre={L.demanderCorrection} onFermer={() => setDemande(false)}>
          <Champ label={L.queCorriger}><input value={champ} onChange={(e) => setChamp(e.target.value)} placeholder={L.queCorrigerPlaceholder} /></Champ>
          <div className="grille g2" style={{ marginTop: 12 }}>
            <Champ label={L.valeurEnregistree} aide={L.valeurEnregistreeAide}>
              <input value={valeurActuelle} onChange={(e) => setValeurActuelle(e.target.value)} placeholder="68 400" />
            </Champ>
            <Champ label={L.valeurCorrecte} aide={L.valeurCorrecteAide}>
              <input value={valeurDemandee} onChange={(e) => setValeurDemandee(e.target.value)} placeholder="64 800" />
            </Champ>
          </div>
          <div style={{ marginTop: 12 }}>
            <Champ label={L.pourquoi}><textarea value={motif} onChange={(e) => setMotif(e.target.value)} placeholder={L.pourquoiPlaceholder} /></Champ>
          </div>
          {champ.length >= 2 && EST_FINANCIER.test(champ) && (
            <div style={{ marginTop: 12 }}>
              <Bandeau type="warn" titre={L.correctionFinanciere}>
                {L.toucheArgent}
              </Bandeau>
            </div>
          )}
          <div className="actions" style={{ marginTop: 14, justifyContent: 'flex-end' }}>
            <button className="btn" onClick={() => setDemande(false)}>{L.annuler}</button>
            <button className="btn btn-p"
                    disabled={champ.length < 2 || motif.length < 3 || valeurDemandee.trim().length === 0}
                    onClick={() => void envoyerDemande()}>{L.envoyerDemande}</button>

          </div>
        </Modale>
      )}
    </>
  );
}
